import type { StyleSpecification } from 'maplibre-gl'

// XingRoute web 端的天地图 MapLibre style 生成器。
// 天地图官方 WMTS 提供 EPSG:3857 raster tiles，按用途细分为 6 个 layer：
//   vec_w / cva_w → 矢量底图 + 中文注记
//   img_w / cia_w → 卫星影像 + 中文注记
//   ter_w / cta_w → 地形（已包含等高线与阴影；自带底色） + 户外注记
// 子域 t0–t7 用于浏览器并发分散。所有 URL 通过 build 时注入的
// VITE_TIANDITU_KEY 携带 `tk=` 参数；token 缺失时 buildTiandituStyle
// 直接返回 null，调用方应回退到 mapbox 或显示 placeholder。
//
// MapStyle 映射（与 RouteMap 的 3 个 style key 对齐，
// 见 wiki/decisions/05-basemaps.md §Web BasemapProvider）：
//   standard  → vec_w + cva_w (默认)
//   outdoor   → vec_w + ter_w (opacity 0.45 overlay) + cva_w
//                ter_w 覆盖稀疏（许多区域返回空瓦片），所以以
//                vec_w 作为完整底图，把 ter_w 当作半透明地形阴影 /
//                等高线 overlay 叠在上面，注记仍用 cva_w 与底图配套。
//                注：天地图不发布单独的"等高线 only" layer；半透明
//                ter_w 是工程上最接近的等价方案。
//   satellite → img_w + cia_w
//
// Reference 天地图官方 WMTS 文档:
//   https://lbs.tianditu.gov.cn/server/MapService.html

const TIANDITU_TOKEN = (import.meta.env.VITE_TIANDITU_KEY ?? '').trim()
const SUBDOMAINS = ['0', '1', '2', '3', '4', '5', '6', '7'] as const

export type TiandituStyleKey = 'standard' | 'outdoor' | 'satellite'

/** 2026-08-30 核对天地图官网首页当前配置后对齐的公开展示值。Web 实际调用
 * `t0`–`t7.tianditu.gov.cn` 官方 WMTS；各服务产品适用范围、影像是否共用及 Logo
 * 要求仍等待官方书面答复。iOS 侧对应 `TiandituAttribution`。 */
export const TIANDITU_MAP_REVIEW_NUMBER = 'GS(2025)1508号'
/** 测绘资质证书号。国内在线地图页脚惯例与审图号连写。 */
export const TIANDITU_SURVEY_QUALIFICATION = '甲测资字11110974'
/** 天地图 source attribution：来源 + 审图号 + 甲测资字（中国《地图审核管理规定》+ 2024 移动 App
 * 地图合规通知要求审图号显著标注，不能用 Mapbox 署名代替）。天地图 logo/商标需另行授权，只用文字。 */
export const TIANDITU_ATTRIBUTION = `© 天地图 · 审图号:${TIANDITU_MAP_REVIEW_NUMBER} - ${TIANDITU_SURVEY_QUALIFICATION}`

interface TiandituLayer {
  /** WMTS service path (`vec_w` / `cva_w` / `img_w` / `cia_w` / `ter_w` / `cta_w`). */
  service: string
  /** WMTS LAYER parameter (`vec` / `cva` / `img` / `cia` / `ter` / `cta`). */
  layer: string
  /** raster-opacity (0–1). Default 1 (opaque). Use a lower value to render
   *  the layer as a translucent overlay over previous layers. */
  opacity?: number
}

const LAYERS: Record<TiandituStyleKey, TiandituLayer[]> = {
  standard: [
    { service: 'vec_w', layer: 'vec' },
    { service: 'cva_w', layer: 'cva' },
  ],
  outdoor: [
    { service: 'vec_w', layer: 'vec' },
    { service: 'ter_w', layer: 'ter', opacity: 0.45 },
    { service: 'cva_w', layer: 'cva' },
  ],
  satellite: [
    { service: 'img_w', layer: 'img' },
    { service: 'cia_w', layer: 'cia' },
  ],
}

function tileUrlsFor(layer: TiandituLayer): string[] {
  if (!TIANDITU_TOKEN) return []
  // MapLibre supports `{x}/{y}/{z}` placeholders in tile URLs but the
  // TianDiTu WMTS endpoint uses `TILEMATRIX={z}&TILEROW={y}&TILECOL={x}`
  // query-string params — and MapLibre will NOT expand `{0-7}` subdomain
  // placeholders. We expand the subdomain ourselves (8 URLs) and leave
  // `{z}/{x}/{y}` for MapLibre to substitute at tile-fetch time.
  // See wiki/pitfalls/maplibre-tile-template-placeholders.md.
  return SUBDOMAINS.map(
    (sub) =>
      `https://t${sub}.tianditu.gov.cn/${layer.service}/wmts?` +
      `SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer.layer}&STYLE=default&` +
      `TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${TIANDITU_TOKEN}`,
  )
}

/**
 * Build a MapLibre style spec for the requested TianDiTu style key.
 * Returns null when the build-time token is missing (caller should fall
 * back to mapbox or render a placeholder).
 */
export function buildTiandituStyle(key: TiandituStyleKey): StyleSpecification | null {
  if (!TIANDITU_TOKEN) return null
  const layers = LAYERS[key]
  const sources: StyleSpecification['sources'] = {}
  const styleLayers: StyleSpecification['layers'] = []
  layers.forEach((layer, index) => {
    const sourceId = `tianditu-${layer.service}`
    sources[sourceId] = {
      type: 'raster',
      tiles: tileUrlsFor(layer),
      tileSize: 256,
      minzoom: 0,
      maxzoom: 18,
      attribution: TIANDITU_ATTRIBUTION,
    }
    styleLayers.push({
      id: `tianditu-${layer.service}-${index}`,
      type: 'raster',
      source: sourceId,
      paint: {
        'raster-resampling': 'linear',
        ...(typeof layer.opacity === 'number' ? { 'raster-opacity': layer.opacity } : {}),
      },
    })
  })
  return {
    version: 8,
    sources,
    layers: styleLayers,
    // Raster-only TianDiTu style: omit glyphs to avoid non-official font endpoints.
  }
}

/** Token availability check used by UI to decide whether to expose the
 * TianDiTu provider as a choice. */
export function isTiandituAvailable(): boolean {
  return TIANDITU_TOKEN.length > 0
}
